import { Alert, Box, Button, CloseButton, Group, Paper, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconCertificate, IconSpeakerphone, IconWallet } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, izvedi, type Shema } from '../api/odjemalec';
import { varnHtml } from '../postavitev/varnHtml';
import { useSeja } from '../seja/useSeja';
import { useZacetna } from './zacetna';
import { barve } from '../tema';

type Opozorilo = Shema['Opozorilo'];

/**
 * Opozorila (naročnina s povezavo na zaslon Naročnina, potek certifikata za FURS) in najnovejša neprebrana novica na
 * vrhu začetnega zaslona. »Razumem« ali × novico zapre za tega uporabnika, nato se pokaže naslednja.
 */
export function ObvestilaZacetne() {
  const zacetna = useZacetna();
  const odjemalec = useQueryClient();
  const stanje = useSeja();
  const prijavaKot = stanje.status === 'prijavljen' && stanje.prijavaKot;
  const [skrite, setSkrite] = useState<number[]>([]);
  const [dela, setDela] = useState(false);
  const novica = zacetna.data?.novice?.find((n) => !skrite.includes(n.id!));
  const opozorila = zacetna.data?.opozorila ?? [];
  if (!opozorila.length && !novica) {
    return null;
  }

  const zapri = async (id: number) => {
    setDela(true);
    setSkrite((s) => [...s, id]);
    try {
      await izvedi(api.POST('/api/novice/{id}/prebrana', { params: { path: { id } } }));
      await odjemalec.invalidateQueries({ queryKey: ['zacetna'] });
    } catch {
      // ob naslednji prijavi se pokaže znova
    } finally {
      setDela(false);
    }
  };

  return (
    <Stack gap="sm" w="100%" maw={880} mb="md">
      {opozorila.map((o) => (
        <OpozoriloKartica key={o.vrsta} o={o} />
      ))}
      {novica && (
        <Paper radius="lg" p="md" shadow="md" role="region" aria-label="Novica">
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
            <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
              <IconSpeakerphone size={22} style={{ flexShrink: 0, marginTop: 2 }} />
              <Box style={{ minWidth: 0 }}>
                <Text fw={700}>{novica.naslov}</Text>
                <Text
                  size="sm"
                  component="div"
                  className="novica"
                  dangerouslySetInnerHTML={{ __html: varnHtml(novica.besedilo ?? '') }}
                />
              </Box>
            </Group>
            <CloseButton aria-label="Zapri novico" onClick={() => void zapri(novica.id!)} disabled={dela} />
          </Group>
          <Group justify="flex-end" mt="xs">
            <Button size="sm" onClick={() => void zapri(novica.id!)} loading={dela}>
              Razumem
            </Button>
          </Group>
          {prijavaKot && (
            <Text size="xs" c="dimmed" ta="right">
              Med prijavo kot uporabnik se novica pri uporabniku ne zapre.
            </Text>
          )}
        </Paper>
      )}
    </Stack>
  );
}

function OpozoriloKartica({ o }: { o: Opozorilo }) {
  return (
    <Alert
      variant="filled"
      color={o.resnost === 'napaka' ? 'red' : 'yellow'}
      radius="lg"
      title={o.naslov}
      icon={
        o.vrsta === 'CERTIFIKAT' ? (
          <IconCertificate size={22} />
        ) : o.vrsta === 'NAROCNINA' ? (
          <IconWallet size={22} />
        ) : (
          <IconAlertTriangle size={22} />
        )
      }
      styles={
        o.resnost === 'napaka'
          ? { title: { fontSize: 15 } }
          : {
              title: { fontSize: 15, color: barve.besedilo },
              message: { color: barve.besedilo },
              icon: { color: barve.besedilo },
            }
      }
    >
      {o.besedilo}
      {o.vrsta === 'NAROCNINA' && (
        <Group mt="xs">
          <Button
            component={Link}
            to="/narocnina"
            size="xs"
            variant={o.resnost === 'napaka' ? 'white' : 'filled'}
            color={o.resnost === 'napaka' ? 'red' : 'dark'}
          >
            Naročnina in podatki za nakazilo
          </Button>
        </Group>
      )}
    </Alert>
  );
}
